<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  appendItemsToOrder,
  getOrder,
  getOrderItems,
  getOrderSettings,
  updateOrderStatus,
} from '../../services/orderApi'
import ALoader from '../common/ALoader.vue'
import ItemItem from './ItemItem.vue'
import { OrderInterface, OrderType } from '../../models/OrderModel'
import ItemDetails from './ItemDetails.vue'
import { ItemInterface } from '../../models/ItemModel'
import { convertProductsToAppendProducts, ProductInterface } from '../../models/ProductModel'
import { getProducts } from '../../services/saleApi'
import { PaginatedResult } from '../../services/api'
import { Modal } from 'bootstrap'
import SaleProductEdit from '../sales/SaleProductEdit.vue'

type BsModal = InstanceType<typeof Modal>

const props = defineProps<{ id: string }>()

const isLoading = ref<boolean>(true)
const isChangingStatus = ref<boolean>(true)
const isLoadingNewProducts = ref<boolean>(true)
const isCollecting = ref<boolean>(false)
const items = ref<ItemInterface[]>([])
const order = ref<OrderInterface | null>(null)
const enableAddProducts = ref<boolean>(false)
const newProduct = ref<ProductInterface | null>(null)
const allFoundProducts = ref<ProductInterface[]>([])
const addNewProductConfirmModalRef = ref<HTMLElement | null>(null)
const addNewProductConfirmModal = ref<BsModal | null>(null)
const updateProductListModalRef = ref<HTMLElement | null>(null)
const updateProductListModal = ref<BsModal | null>(null)
const showNewProductDetails = ref<boolean>(false)
const searchInput = ref<string>('')
const searchQuery = ref<string>('')
const activeItem = ref<ItemInterface | null>(null)
let searchDebounceTimer: number | null = null
const enterQueued = ref<boolean>(false)
const debugEnabled = !!(import.meta.env.VITE_DEBUG as number)
const canCaptureFocus = computed(() => {
  return isCollecting.value && !activeItem.value
})

let focusGuardTimer: number | null = null
const FOCUS_GUARD_MS = 500

function isAllowedFocusedElement(el: Element | null): boolean {
  return el === captureEl.value || el === searchEl.value
}

function ensureScannerFocus() {
  if (!canCaptureFocus.value) return

  const active = document.activeElement
  if (isAllowedFocusedElement(active)) return

  void focusCapture()
}

function startFocusGuard() {
  stopFocusGuard()

  focusGuardTimer = window.setInterval(() => {
    ensureScannerFocus()
  }, FOCUS_GUARD_MS)
}

function stopFocusGuard() {
  if (focusGuardTimer) {
    window.clearInterval(focusGuardTimer)
    focusGuardTimer = null
  }
}

watch(
  () => canCaptureFocus.value,
  (canFocus) => {
    if (canFocus) {
      void focusCapture()
      startFocusGuard()
      return
    }

    stopFocusGuard()
  },
  { immediate: true }
)

async function searchForNewProduct(): Promise<void> {
  isLoadingNewProducts.value = true
  const result: PaginatedResult<ProductInterface> = await getProducts(1, searchQuery.value)

  allFoundProducts.value = result.items

  if (result.items.length === 1) {
    newProduct.value = result.items[0]
  }

  isLoadingNewProducts.value = false

  return null
}

function startAddingProduct() {
  showNewProductDetails.value = true
  closeAddNewProductConfirmModal()
}

function refreshItems() {
  isLoading.value = true
  getOrderItems(props.id)
    .then((parsedItems) => {
      items.value = parsedItems
    })
    .finally(() => {
      isLoading.value = false
    })
}

function finishAddingProduct() {
  isLoading.value = true
  const appendProducts = convertProductsToAppendProducts([newProduct.value])
  appendItemsToOrder(props.id, appendProducts)
    .then(() => {
      closeUpdateNewProductConfirmModal()
      newProduct.value = null
      showNewProductDetails.value = false
      refreshItems()
    })
    .finally(() => {
      isLoading.value = false
    })
}

watch(searchInput, (value, oldValue) => {
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer)

  searchDebounceTimer = window.setTimeout(() => {
    searchQuery.value = value.trim()
    nextTick(async () => {
      if (value.length - oldValue.length > 1) {
        enterQueued.value = true
      }
      if (
        filteredItems.value.length === 0 &&
        enableAddProducts &&
        order.value.type === OrderType.outbound
      ) {
        await searchForNewProduct()
      }
      if (
        (filteredItems.value.length === 0 ||
          (filteredItems.value.length === 1 && filteredItems.value[0]?.is_added === true)) &&
        enterQueued.value &&
        enableAddProducts &&
        order.value.type === OrderType.outbound
      ) {
        await searchForNewProduct()
        if (newProduct.value) {
          openAddNewProductConfirmModal()
        }
        return
      }
      if (!enterQueued.value) return
      if (filteredItems.value.length !== 1) {
        enterQueued.value = false
        return
      }
      activeItem.value = filteredItems.value[0]
      enterQueued.value = false
    })
  }, 250)
})

function itemMatchesQuery(item: ItemInterface, q: string): boolean {
  const query = q.toLowerCase()
  if (!query) return true

  const haystack: string[] = [
    item.title,
    item.order_item_number,
    item.erp_code,
    item.barcode?.main ?? '',
    ...(item.barcode?.list ?? []),
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase())

  return haystack.some((v) => v.includes(query))
}

const filteredItems = computed(() => {
  const q = searchQuery.value
  if (!q) return items.value
  return items.value.filter((item) => itemMatchesQuery(item, q))
})

watch(enterQueued, async () => {
  if (!enterQueued.value) return
  if (filteredItems.value.length === 1 && !filteredItems.value[0]?.is_added === true) {
    activeItem.value = filteredItems.value[0]
    enterQueued.value = false
  }
  if (filteredItems.value.length === 1 && filteredItems.value[0]?.is_added === true) {
    await searchForNewProduct()
    if (newProduct.value) {
      openAddNewProductConfirmModal()
    }
    enterQueued.value = false
    return
  }
  if (
    filteredItems.value.length === 0 &&
    enableAddProducts &&
    order.value.type === OrderType.outbound
  ) {
    await searchForNewProduct()
    if (newProduct.value) {
      openAddNewProductConfirmModal()
    }
  }
})

function onSearchKeyUp(e: KeyboardEvent) {
  if (e.key !== 'Enter') return
  enterQueued.value = true
}

onMounted(async () => {
  getOrder(props.id)
    .then((parsedOrder) => {
      order.value = parsedOrder
      if (order.value.status === 'collecting') {
        isCollecting.value = true
        return
      }
      startCollection()
    })
    .finally(() => {
      isChangingStatus.value = false
    })

  getOrderItems(props.id)
    .then((parsedItems) => {
      items.value = parsedItems
    })
    .finally(() => {
      isLoading.value = false
      void focusCapture()
    })

  getOrderSettings().then((parsedSettings) => {
    enableAddProducts.value = parsedSettings.enableAddProducts || false
  })

  if (addNewProductConfirmModalRef.value) {
    addNewProductConfirmModal.value = new Modal(addNewProductConfirmModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }

  if (updateProductListModalRef.value) {
    updateProductListModal.value = new Modal(updateProductListModalRef.value, {
      backdrop: true,
      keyboard: true,
    })
  }
})

function openAddNewProductConfirmModal() {
  searchInput.value = ''
  addNewProductConfirmModal.value?.show()
}

function closeAddNewProductConfirmModal() {
  addNewProductConfirmModal.value?.hide()
}

// function openUpdateNewProductConfirmModal() {
//   updateProductListModal.value?.show()
// }

function closeUpdateNewProductConfirmModal() {
  updateProductListModal.value?.hide()
}

function onProductAmountInput(product: ProductInterface) {
  if (product.quantity === 0) {
    showNewProductDetails.value = false
    newProduct.value = null
    return
  }
  newProduct.value = product
  finishAddingProduct()
  // openUpdateNewProductConfirmModal()
}

async function startCollection(): Promise<void> {
  isChangingStatus.value = true
  updateOrderStatus(props.id, 'collecting')
    .then(async () => {
      isCollecting.value = true
    })
    .finally(() => {
      isChangingStatus.value = false
    })
}

function showItemDetails(item: ItemInterface) {
  activeItem.value = item
}

function closeItemDetails() {
  searchInput.value = ''
  activeItem.value = null
}

function updateItem() {
  refreshItems()
  // const idx = items.value.findIndex((item) => item.id === updated.id)
  // if (idx !== -1) {
  //   items.value[idx] = updated
  // }
  //
  // if (activeItem.value?.id === updated.id) {
  //   activeItem.value = updated
  // }
}

const captureEl = ref<HTMLInputElement | null>(null)
const searchEl = ref<HTMLInputElement | null>(null)
const captureValue = ref<string>('')
let commitTimer: number | null = null
const COMMIT_AFTER_MS = 120

function scheduleCommit() {
  if (commitTimer) window.clearTimeout(commitTimer)
  commitTimer = window.setTimeout(() => commitScan(), COMMIT_AFTER_MS)
}

async function commitScan() {
  const value = (captureValue.value ?? '').trim()
  if (!value) return

  enterQueued.value = true
  await nextTick()
  searchInput.value = value

  captureValue.value = ''
  void focusCapture()
}

async function focusCapture() {
  await nextTick()
  if (!canCaptureFocus.value) return

  const el = captureEl.value
  if (!el) return

  try {
    el.focus({ preventScroll: true } as any)
  } catch {
    el.focus()
  }

  try {
    const len = el.value.length
    el.setSelectionRange(len, len)
  } catch {
    // ignore
  }
}

function decodeKey(e: KeyboardEvent): string | null {
  if (typeof e.key === 'string' && e.key) {
    if (e.key === 'Enter') return 'ENTER'
    if (e.key === 'Tab') return 'TAB'
    if (e.key.length === 1) return e.key
    return null
  }

  const anyE = e as any
  const code: number | undefined = typeof anyE.keyCode === 'number' ? anyE.keyCode : anyE.which
  if (typeof code !== 'number') return null

  if (code === 13) return 'ENTER'
  if (code === 9) return 'TAB'

  if (code >= 48 && code <= 57) return String.fromCharCode(code)
  if (code >= 96 && code <= 105) return String(code - 96)

  if (code >= 65 && code <= 90) {
    const ch = String.fromCharCode(code)
    return e.shiftKey ? ch : ch.toLowerCase()
  }

  if (code === 189 || code === 109) return e.shiftKey ? '_' : '-'
  if (code === 190 || code === 110) return '.'
  if (code === 191 || code === 111) return '/'

  return null
}

function onCaptureKeydown(e: KeyboardEvent) {
  if (!canCaptureFocus.value) return

  const decoded = decodeKey(e)
  if (!decoded) return

  if (decoded === 'ENTER' || decoded === 'TAB') {
    e.preventDefault()
    e.stopPropagation()
    commitScan()
    return
  }

  captureValue.value += decoded
  scheduleCommit()
}

function onCaptureBlur() {
  window.setTimeout(() => {
    if (!canCaptureFocus.value) return

    const active = document.activeElement
    if (active === searchEl.value) return
    if (active === captureEl.value) return

    void focusCapture()
  }, 0)
}

function onSearchBlur() {
  window.setTimeout(() => void focusCapture(), 0)
}

function selectNewProduct(product: ProductInterface) {
  newProduct.value = product
  openAddNewProductConfirmModal()
}

watch(
  () => activeItem.value,
  async (val) => {
    if (val) {
      captureValue.value = ''
      if (commitTimer) window.clearTimeout(commitTimer)
      return
    }

    await nextTick()
    void focusCapture()
  }
)

watch(
  () => isCollecting.value,
  (v) => {
    if (v) void focusCapture()
  }
)

onMounted(() => {
  if (canCaptureFocus.value) void focusCapture()
})

onBeforeUnmount(() => {
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer)
  if (commitTimer) window.clearTimeout(commitTimer)
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer)
  if (commitTimer) window.clearTimeout(commitTimer)
  stopFocusGuard()
})
</script>

<template>
  <div>
    <ALoader v-if="isLoading || isChangingStatus" />
    <div v-else>
      <div v-if="isCollecting" class="mb-24">
        <input
          ref="captureEl"
          v-model="captureValue"
          class="form-control search"
          :class="{ 'scanner-capture': !debugEnabled }"
          type="text"
          autocomplete="off"
          spellcheck="false"
          inputmode="none"
          enterkeyhint="done"
          aria-hidden="true"
          tabindex="-1"
          @keydown="onCaptureKeydown"
          @blur="onCaptureBlur"
        />

        <input
          ref="searchEl"
          v-model="searchInput"
          type="search"
          class="form-control search"
          :placeholder="$t('search')"
          :disabled="showNewProductDetails"
          autocomplete="off"
          spellcheck="false"
          @keyup="onSearchKeyUp"
          @blur="onSearchBlur"
        />
      </div>

      <template v-if="!showNewProductDetails">
        <div v-if="filteredItems.length" class="d-flex flex-column">
          <template v-for="(item, index) in filteredItems" :key="item.id ?? index">
            <ItemItem
              :item="item"
              :can-collect="isCollecting"
              @item-click="showItemDetails(item)"
            />
          </template>
        </div>

        <div v-else-if="allFoundProducts.length">
          <h5 class="c-primary">{{ $t('suggested-products') }}:</h5>
          <div
            v-for="(product, productIndex) in allFoundProducts"
            :key="product.id ?? productIndex"
            class="customer-item"
          >
            <button type="button" @click="selectNewProduct(product)">
              <b>{{ product.name }}</b>
              <small
                >{{ $t('code') }}:&nbsp;{{ product.code }} | {{ $t('barcode') }}:&nbsp;{{
                  product.barcode
                }}</small
              >
            </button>
          </div>
        </div>

        <ALoader v-else-if="isLoadingNewProducts" />

        <div v-else>
          <h5 class="text-center text-primary my-32">
            {{ $t('no-documents') }}
          </h5>
        </div>

        <ItemDetails
          v-if="activeItem"
          :item="activeItem"
          @close-details="closeItemDetails"
          @update-item="updateItem"
        />
      </template>

      <template v-else>
        <SaleProductEdit
          v-if="newProduct"
          :product="newProduct"
          @product-updated="onProductAmountInput"
        />
      </template>
    </div>

    <div ref="addNewProductConfirmModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <h4 class="mb-16">{{ $t('add-new-product-confirm-header') }}</h4>
            <div v-if="newProduct" class="mb-32">
              <p class="mb-12">{{ $t('add-new-product-confirm-text') }}</p>
              <b>{{ newProduct.name }}</b>
              <div>
                {{ $t('code') }}:&nbsp;{{ newProduct.code }} | {{ $t('barcode') }}:&nbsp;{{
                  newProduct.barcode
                }}
              </div>
            </div>
            <div class="d-flex flex-wrap gap-12">
              <button
                type="button"
                class="btn btn-outline-secondary flex-1"
                data-bs-dismiss="modal"
              >
                {{ $t('no') }}
              </button>
              <button type="button" class="btn btn-danger flex-1" @click="startAddingProduct">
                {{ $t('yes') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div ref="updateProductListModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <h4 class="mb-16">{{ $t('add-new-product-confirm-header') }}</h4>
            <div v-if="newProduct" class="mb-32">
              <p class="mb-12">{{ $t('add-new-product-confirm-text') }}</p>
              <b>{{ newProduct.name }}</b>
              <div>
                {{ $t('code') }}:&nbsp;{{ newProduct.code }} | {{ $t('barcode') }}:&nbsp;{{
                  newProduct.barcode
                }}
              </div>
              <div>{{ $t('amount') }}:&nbsp;{{ newProduct.quantity }}</div>
            </div>
            <div class="d-flex flex-wrap gap-12">
              <button
                type="button"
                class="btn btn-outline-secondary flex-1"
                data-bs-dismiss="modal"
              >
                {{ $t('no') }}
              </button>
              <button type="button" class="btn btn-danger flex-1" @click="finishAddingProduct">
                {{ $t('yes') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.scanner-capture {
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
</style>
